import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { Pencil } from "lucide-react";
import type { SystemConfigDto } from "@/features/admin/api/dto";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (= la clé). */
export type SystemConfigRow = SystemConfigDto & { id: string };

/**
 * Colonnes des clés de configuration système (`GET /SystemConfigs`).
 * L'action d'édition (`PUT /SystemConfigs/{key}`) est réservée aux rôles
 * flashpay-superadmin / flashpay-admin (§6.8) — masquée via `RequireRole`.
 * Clés et valeurs en `NoTranslate`.
 */
export const systemConfigsColumns = (
  onEdit: (config: SystemConfigRow) => void
): ColumnsClarFactory<SystemConfigRow> => () => [
  {
    id: "key",
    accessorKey: "key",
    header: t("admin.configs.columns.key"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "value",
    accessorKey: "value",
    header: t("admin.configs.columns.value"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="line-clamp-1 font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "description",
    accessorKey: "description",
    header: t("admin.configs.columns.description"),
    cell: ({ getValue }) => (
      <span className="line-clamp-1 text-sm text-muted-foreground">
        {(getValue() as string | null | undefined) || "—"}
      </span>
    ),
    meta: { type: "text" },
  },
  {
    id: "updatedByKeycloakId",
    accessorKey: "updatedByKeycloakId",
    header: t("admin.configs.columns.updatedBy"),
    cell: ({ getValue }) => {
      const id = getValue() as string | undefined | null;
      return (
        <NoTranslate as="span" className="font-mono text-xs">
          {id ? `${id.slice(0, 8)}…` : "—"}
        </NoTranslate>
      );
    },
    meta: { type: "text" },
  },
  {
    id: "updatedAt",
    accessorKey: "updatedAt",
    header: t("admin.configs.columns.updatedAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) => (
      <RequireRole roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}>
        <RowActionsMenu
          actions={[
            {
              id: "edit",
              label: t("admin.configs.actions.edit"),
              icon: Pencil,
              onClick: () => onEdit(row.original),
            },
          ]}
        />
      </RequireRole>
    ),
    meta: { type: "denied" },
  },
];
